import { format } from 'date-fns';

type FormatArgs = {
  data: any[];
  filter?: any;
};

const getInterventionFields = (interventions: any, filter?: any) => {
  let interventionEndDates = new Set(),
    interventionFocusSubjects = new Set(),
    interventionGrades = new Set(),
    interventionIds = new Set(),
    interventionNames = new Set(),
    interventionProgrammes = new Set(),
    interventionSPs = new Set(),
    interventionStartDates = new Set(),
    interventionSubjects = new Set();

  interventions.forEach((value: any) => {
    const { focusSubjects, fromDate, grades, id, name, programme, serviceProviders, subjects, toDate } = value;

    if (filter && filter.interventionIds && !filter.interventionIds.includes(id)) return;

    if (focusSubjects) {
      focusSubjects.forEach((focusSubject: any) => {
        interventionFocusSubjects.add(focusSubject.name);
      });
    }
    if (fromDate) interventionStartDates.add(format(new Date(fromDate), 'yyyy-MM-dd'));
    if (grades) {
      grades.forEach((grade: any) => {
        interventionGrades.add(grade.value);
      });
    }
    if (id) interventionIds.add(id);
    if (name) interventionNames.add(name);
    if (programme) {
      if (filter && filter.programmeIds) {
        if (filter.programmeIds.includes(programme.id)) interventionProgrammes.add(programme.name);
      } else {
        interventionProgrammes.add(programme.name);
      }
    }
    if (serviceProviders) {
      serviceProviders.forEach((sp: any) => {
        if (filter && filter.serviceProviderIds) {
          if (filter.serviceProviderIds.includes(sp.id)) interventionSPs.add(sp.name);
        } else {
          interventionSPs.add(sp.name);
        }
      });
    }
    if (subjects) {
      subjects.forEach((subject: any) => {
        interventionSubjects.add(subject.subjectGradeReference);
      });
    }
    if (toDate) interventionEndDates.add(format(new Date(toDate), 'yyyy-MM-dd'));
  });

  return {
    interventionEndDates,
    interventionFocusSubjects,
    interventionGrades,
    interventionIds,
    interventionNames,
    interventionProgrammes,
    interventionSPs,
    interventionStartDates,
    interventionSubjects,
  };
};

export const formatStakeholdersProfileData = ({ data }: FormatArgs) => {
  let results: any[] = [];

  data.forEach(edge => {
    const stakeholder = edge.node;
    const { grades, institutions, interventions, subjects, ...rest } = stakeholder;
    let interventionFields: any = {};

    if (interventions && interventions.length) {
      interventionFields = getInterventionFields(interventions);
    }

    results.push({
      additionalInstitutions: (institutions && institutions.forEach((value: any) => value.name).join(', ')) || '',
      grades: (grades && grades.forEach((grade: any) => grade.value).join(', ')) || '',
      subjects: (subjects && subjects.forEach((subject: any) => subject.subjectGradeReference).join(', ')) || '',
      ...rest,
      // interventionFields
      programmeName: Array.from(interventionFields.interventionProgrammes || []).join(', '),
      serviceProviderName: Array.from(interventionFields.interventionSPs || []).join(', '),
      interventionIds: Array.from(interventionFields.interventionIds || []).join(', '),
      interventionName: Array.from(interventionFields.interventionNames || []).join(', '),
      interventionSubject: Array.from(interventionFields.interventionSubjects || []).join(', '),
      interventionGrades: Array.from(interventionFields.interventionGrades || []).join(', '),
      interventionStartDate: Array.from(interventionFields.interventionStartDates || []).join(', '),
      interventionEndDate: Array.from(interventionFields.interventionEndDates || []).join(', '),
    });
  });

  return results;
};

export const formatInstitutionsProfileData = ({ data, filter }: FormatArgs) => {
  let results: any[] = [];

  data.forEach(edge => {
    const {
      circuit,
      country,
      district,
      interventionRole,
      interventions,
      phase,
      province,
      region,
      sector,
      specializationGroup,
      type,
      ...rest
    } = edge.node;
    let interventionFields: any = {};

    if (interventions && interventions.length) {
      interventionFields = getInterventionFields(interventions, filter);
    }

    results.push({
      // institutions fields
      institutionType: type && type.name,
      province: province && province.name,
      region: region && region.name,
      district: district && district.name,
      circuit: circuit && circuit.name,
      country: country && country.name,
      sector: sector && sector.name,
      phase: phase && phase.name,
      specializationGroup: specializationGroup && specializationGroup.name,
      interventionRole: interventionRole && interventionRole.name,
      ...rest,
      // intervention fields
      programmeNames: Array.from(interventionFields.interventionProgrammes || []).join(', '),
      serviceProviderNames: Array.from(interventionFields.interventionSPs || []).join(', '),
      interventionIds: Array.from(interventionFields.interventionIds || []).join(', '),
      interventionNames: Array.from(interventionFields.interventionNames || []).join(', '),
      interventionSubjects: Array.from(interventionFields.interventionSubjects || []).join(', '),
      interventionFocusSubjects: Array.from(interventionFields.interventionFocusSubjects || []).join(', '),
      interventionGrades: Array.from(interventionFields.interventionGrades || []).join(', '),
    });
  });

  return results;
};

export const formatInterventionsProfileData = ({ data }: FormatArgs) => {
  let results: any[] = [];

  data.forEach(edge => {
    const {
      focus,
      fromDate,
      grades,
      impactThemes,
      outcomeThemes,
      serviceProviders,
      subjects,
      toDate,
      ...rest
    } = edge.node;

    results.push({
      endDate: toDate && format(new Date(toDate), 'yyyy-MM-dd'),
      foci: focus && focus.map((value: any) => value.name).join('; '),
      grades: grades && grades.map((value: any) => value.value).join('; '),
      impactThemes: impactThemes && impactThemes.map((value: any) => value.name).join('; '),
      outcomeThemes: outcomeThemes && outcomeThemes.map((value: any) => value.name).join('; '),
      serviceProviders: serviceProviders && serviceProviders.map((value: any) => value.name).join('; '),
      startDate: fromDate && format(new Date(fromDate), 'yyyy-MM-dd'),
      subjects: subjects && subjects.map((value: any) => value.subjectGradeReference).join('; '),
      ...rest,
    });
  });

  return results;
};
